<style lang="scss">
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/utils";
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/colors";

  .license-table-container {
    width: 100%;
  }

  .user-count {
    font-size: utils.in_rem(12px);
    color: colors.get(textGrey);
  }

  .filter-container {
    display: flex;
    align-items: center;
    gap: utils.in_rem(12px);
    margin-bottom: utils.in_rem(16px);
    justify-content: space-between;
  }
</style>

<script>
  import Search from "@sourcewhale/shared/svelte-design-system/components/atoms/Search/Search.svelte";
  import DataTable from "@sourcewhale/shared/svelte-design-system/components/molecules/DataTable/DataTable.svelte";
  import MultiSelectDropdown from "@sourcewhale/shared/svelte-design-system/components/molecules/Dropdown/MultiSelectDropdown/MultiSelectDropdown.svelte";
  import { teamStore } from "./AddUserModal/stores";
  import LicenseCheckbox from "./TableComponents/LicenseCheckbox.svelte";
  import Notification from "./TableComponents/Notification.svelte";
  import UserCell from "./TableComponents/UserCell.svelte";

  import { createMultiCheckboxContext } from "@sourcewhale/shared/svelte-design-system/components/atoms/Checkbox/context.svelte";
  import PaginationGroup from "@sourcewhale/shared/svelte-design-system/components/molecules/Pagination/PaginationGroup.svelte";
  import { getAdminPanelContext } from "./context";

  const { rows, modulesStore } = getAdminPanelContext();

  // Create the derived store directly in the component

  // Search and filter state
  let searchTerm = $state("");
  let selectedGroups = $state([]);
  let order = $state([]);

  let outreachRemovals = $derived(
    $rows.filter(
      (row) => row.originalLicenses.outreach && !row.licenses.outreach
    )
  );

  let filterAffectedUsers = $state(false);

  // Count users who had any licenses and now have none
  let noLicensesUsers = $derived(
    $rows.filter((row) => {
      const hadLicenses = Object.values(row.originalLicenses).some(Boolean);
      const hasNoLicenses = !Object.values(row.licenses).some(Boolean);
      return hadLicenses && hasNoLicenses;
    })
  );
  // Filter data based on search term and selected group
  let filteredData = $derived.by(() =>
    ($rows || []).filter((user) => {
      const nameMatch =
        !searchTerm ||
        (user.name &&
          user.name.toLowerCase().includes(searchTerm.toLowerCase()));

      const groupMatch =
        selectedGroups.length === 0 ||
        user.groupIds.some((groupId) => selectedGroups.includes(groupId));

      const affectedUserIds = [...outreachRemovals, ...noLicensesUsers].map(
        (u) => u.userId
      );

      const affectedUserMatch =
        !filterAffectedUsers ||
        affectedUserIds.length === 0 ||
        affectedUserIds.includes(user.userId);

      return nameMatch && groupMatch && affectedUserMatch;
    })
  );

  // pagination
  let start = $state(0);
  let length = $state(10);
  let data = $derived(filteredData.slice(start, start + length));

  let columns = $derived.by(() => {
    const baseColumns = [
      {
        name: "user",
        title: "User",
        data: (row) => ({
          email: row.email,
          name: row.name,
          photo: row.photo,
          userId: row.userId,
          licenses: row.licenses,
          originalLicenses: row.originalLicenses,
        }),
        className: "sticky-left",
        sticky: "left",
        orderable: true,
      },
    ];

    $modulesStore.forEach((m) => {
      if (m.permissionObject === "team") return;
      // TODO remove
      if (["alias", "viewer"].includes(m.kind)) return;
      baseColumns.push({
        name: m.kind === "data" ? "dataLicense" : m.kind,
        title: m.title,
        data: (row) => row,
        className: "license-column",
      });
    });
    return baseColumns;
  });

  // Count users who had outreach license and now don't

  // https://svelte.dev/docs/svelte/snippet#Snippet-scope
  let restProps = $derived(
    outreachRemovals.length > 0 || noLicensesUsers.length > 0
      ? { notification }
      : {}
  );
</script>

{#snippet notification()}
  <Notification
    outreachCount={outreachRemovals.length}
    noLicensesCount={noLicensesUsers.length}
    showAffectedUsersButton={true}
    onclick={() => {
      filterAffectedUsers = !filterAffectedUsers;
    }}
    {filterAffectedUsers}
  />
{/snippet}

<div class="license-table-container">
  <!-- Search and filter container -->
  <div class="filter-container">
    <Search
      bind:value={searchTerm}
      placeholder="Search by name"
      style="width: 340px;"
    />
    <MultiSelectDropdown
      items={Object.entries($teamStore.teamGroups).map(([id, group]) => ({
        id,
        label: group.groupName,
      }))}
      placeholder="Select groups"
      dropdownStyle="width: 340px;"
      bind:selectedItems={selectedGroups}
    />
  </div>

  <!-- Data table -->
  <DataTable
    {columns}
    {data}
    {order}
    fetching={$teamStore.meta?.fetching}
    onMountRow={createMultiCheckboxContext}
    {...restProps}
  >
    <!-- User cell snippet -->
    {#snippet user(value)}
      <UserCell user={value} />
    {/snippet}

    <!-- License snippets -->

    {#snippet extension(value)}
      <LicenseCheckbox {value} skeleton={false} kind="extension" />
    {/snippet}

    {#snippet outreach(value)}
      <LicenseCheckbox {value} skeleton={false} kind="outreach" />
    {/snippet}

    {#snippet dataLicense(value)}
      <LicenseCheckbox {value} skeleton={false} kind="data" />
    {/snippet}

    {#snippet ausConversations(value)}
      <LicenseCheckbox {value} skeleton={false} kind="ausConversations" />
    {/snippet}

    {#snippet euConversations(value)}
      <LicenseCheckbox {value} skeleton={false} kind="euConversations" />
    {/snippet}

    {#snippet ukConversations(value)}
      <LicenseCheckbox {value} skeleton={false} kind="ukConversations" />
    {/snippet}

    {#snippet usConversations(value)}
      <LicenseCheckbox {value} skeleton={false} kind="usConversations" />
    {/snippet}

    {#snippet projects(value)}
      <LicenseCheckbox {value} skeleton={false} kind="projects" />
    {/snippet}

    {#snippet inboxAgent(value)}
      <LicenseCheckbox {value} skeleton={false} kind="inboxAgent" />
    {/snippet}

    {#snippet notetaker(value)}
      <LicenseCheckbox {value} skeleton={false} kind="notetaker" />
    {/snippet}
  </DataTable>

  <PaginationGroup bind:start bind:length total={filteredData.length} />
</div>
